import type { RefObject } from 'react';
import { useLayoutEffect, useState } from 'react';

import { store } from '@store';

import { HEADER_AND_BOTTOM_RESERVE_HEIGHT, INITIAL_HEIGHT_PAGE_RATIO, MIN_BODY_HEIGHT } from './constants';

function getScrollAncestorScrollTop(element: HTMLElement): number {
    let ancestor = element.parentElement;
    while (ancestor) {
        if (isScrollContainer(ancestor)) {
            return ancestor.scrollTop;
        }
        ancestor = ancestor.parentElement;
    }
    return 0;
}

function measureAvailableHeight(element: HTMLElement): number {
    return document.body.scrollHeight - getUnscrolledTop(element) - HEADER_AND_BOTTOM_RESERVE_HEIGHT;
}

export function useCalculatedHeight(tableRef: RefObject<HTMLDivElement | null>): number {
    const [calculatedHeight, setCalculatedHeight] = useState<number>(
        () => store.viewPort.pageHeight * INITIAL_HEIGHT_PAGE_RATIO,
    );

    useLayoutEffect(() => {
        let measuredTop: number | undefined;

        const measure = (): void => {
            if (tableRef.current) {
                measuredTop = getUnscrolledTop(tableRef.current);
                setCalculatedHeight(measureAvailableHeight(tableRef.current));
            }
        };

        measure();

        let frameId = 0;
        const scheduleMeasure = (): void => {
            cancelAnimationFrame(frameId);
            frameId = requestAnimationFrame(measure);
        };

        // Page chrome above the table (header tabs, content padding) can be applied after the first measure.
        // Re-measure only when the table has actually moved, so its own growth does not trigger a new measure.
        const scheduleMeasureIfMoved = (): void => {
            if (tableRef.current && getUnscrolledTop(tableRef.current) !== measuredTop) {
                scheduleMeasure();
            }
        };

        const resizeObserver = new ResizeObserver(scheduleMeasureIfMoved);
        if (tableRef.current) {
            getAncestorsUpToScrollContainer(tableRef.current).forEach(ancestor => resizeObserver.observe(ancestor));
        }

        window.addEventListener('resize', scheduleMeasure);

        return () => {
            cancelAnimationFrame(frameId);
            resizeObserver.disconnect();
            window.removeEventListener('resize', scheduleMeasure);
        };
    }, [tableRef]);

    return Math.max(MIN_BODY_HEIGHT, calculatedHeight);
}

function getUnscrolledTop(element: HTMLElement): number {
    // Scroll compensation keeps the result stable no matter how the page is scrolled at measure time
    return element.getBoundingClientRect().top + getScrollAncestorScrollTop(element);
}

function getAncestorsUpToScrollContainer(element: HTMLElement): HTMLElement[] {
    const ancestors: HTMLElement[] = [];
    let ancestor = element.parentElement;
    while (ancestor) {
        ancestors.push(ancestor);
        if (isScrollContainer(ancestor)) {
            break;
        }
        ancestor = ancestor.parentElement;
    }
    return ancestors;
}

function isScrollContainer(element: HTMLElement): boolean {
    const { overflowY } = window.getComputedStyle(element);
    return overflowY === 'auto' || overflowY === 'scroll';
}
